import { Directive, forwardRef, Input } from '@angular/core'
import { NG_VALIDATORS, Validator, AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { NativeDateAdapter } from '@angular/material/core'
import { parseTime } from './time-picker-utils'

@Directive({
  selector: 'input[atp-time-picker][min],input[atp-time-picker][max]',
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => APTInputValidatorDirective), multi: true }],
})
export class APTInputValidatorDirective implements Validator {
  @Input() includeMaxMin = false
  maxTime: Date | null
  @Input() set max(value: string | null) {
    const validValue = parseTime(value || '')?.toDate() || null
    if (
      !this.isSameDatetime(validValue, this.maxTime) &&
      (!this.minTime || this.compareDatetime(this.minTime, validValue) <= 0)
    ) {
      this.maxTime = validValue
      this._validatorOnChange()
    }
  }

  minTime: Date | null
  @Input() set min(value: string | null) {
    const validValue = parseTime(value || '')?.toDate() || null

    if (
      !this.isSameDatetime(validValue, this.minTime) &&
      (!this.maxTime || this.compareDatetime(validValue, this.maxTime) <= 0)
    ) {
      this.minTime = validValue
      this._validatorOnChange()
    }
  }

  private isSameDatetime(d1: Date | null, d2: Date | null) {
    if (!this.dateAdapter.sameDate(d1, d2)) return false
    if (!d1 && !d2) return true
    if (!d1 || !d2) return false
    return d1.getHours() === d2.getHours() && d1.getMinutes() === d2.getMinutes()
  }

  private compareDatetime(d1: Date, d2: Date) {
    const res = this.dateAdapter.compareDate(d1, d2)
    if (res !== 0) return res
    return d1.getHours() * 60 + d1.getMinutes() - (d2.getHours() * 60 + d2.getMinutes())
  }

  _validatorOnChange = () => {}

  private _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = parseTime(control.value || '')?.toDate() || null
    const min = this.minTime
    if (!min || !controlValue) return null
    const res = this.compareDatetime(min, controlValue)
    if (this.includeMaxMin && res <= 0) return null
    if (!this.includeMaxMin && res < 0) return null

    return { min: { min: min, actual: controlValue } }
  }

  private _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = parseTime(control.value || '')?.toDate() || null
    const max = this.maxTime
    if (!max || !controlValue) return null
    const res = this.compareDatetime(max, controlValue)
    if (this.includeMaxMin && res >= 0) return null
    if (!this.includeMaxMin && res > 0) return null

    return { max: { max: max, actual: controlValue } }
  }

  protected _getValidators(): ValidatorFn[] {
    return [this._minValidator, this._maxValidator]
  }

  /** The combined form control validator for this input. */
  protected _validator: ValidatorFn | null
  private dateAdapter = new NativeDateAdapter('en-GB')

  constructor() {
    this._validator = Validators.compose(this._getValidators())
  }

  registerOnValidatorChange(fn: () => void): void {
    this._validatorOnChange = fn
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this._validator ? this._validator(c) : null
  }
}
